/**
 * Wrapper d'haptique unifié.
 *
 * Utilise `navigator.vibrate` aujourd'hui (Android Chrome / certains
 * navigateurs mobiles), et sera branché sur `@capacitor/haptics` lorsque
 * l'app sera emballée par Capacitor pour iOS / Android natif.
 *
 * Tous les helpers sont silencieux si aucun moteur n'est disponible
 * (desktop, iOS Safari, etc.).
 */

import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';

let nativeImpactLight: ((opts?: unknown) => void) | null = null;
let nativeImpactMedium: ((opts?: unknown) => void) | null = null;
let nativeNotificationSuccess: (() => void) | null = null;
let nativeAvailable = false;

/**
 * Branche dynamiquement le module Capacitor Haptics si présent dans l'env.
 * Doit être appelé une fois au boot de l'app (no-op si module absent).
 */
export async function initHaptics(): Promise<void> {
  if (nativeAvailable) return;
  if (!Capacitor.isNativePlatform()) return;

  nativeImpactLight = () => { void Haptics.impact({ style: ImpactStyle.Light }); };
  nativeImpactMedium = () => { void Haptics.impact({ style: ImpactStyle.Medium }); };
  nativeNotificationSuccess = () => { void Haptics.notification({ type: NotificationType.Success }); };
  nativeAvailable = true;
}

function vibrate(pattern: number | number[]): void {
  if (typeof navigator === 'undefined') return;
  if (typeof navigator.vibrate !== 'function') return;
  try {
    navigator.vibrate(pattern);
  } catch {
    // certains navigateurs jettent en mode strict / iframe
  }
}

/** Léger feedback : carte sous le doigt, slot dispo, etc. */
export function tap(): void {
  if (nativeAvailable && nativeImpactLight) {
    nativeImpactLight();
    return;
  }
  vibrate(10);
}

/** Feedback de sélection : ecart, toggle. */
export function select(): void {
  if (nativeAvailable && nativeImpactLight) {
    nativeImpactLight();
    return;
  }
  vibrate(15);
}

/** Feedback fort : carte armée prête à être jouée. */
export function arm(): void {
  if (nativeAvailable && nativeImpactMedium) {
    nativeImpactMedium();
    return;
  }
  vibrate(25);
}

/** Feedback de réussite : action validée (carte jouée). */
export function success(): void {
  if (nativeAvailable && nativeNotificationSuccess) {
    nativeNotificationSuccess();
    return;
  }
  vibrate([15, 30, 15]);
}
